import React from 'react';
import { useContent } from '../hooks/useContent';

const SettingsPage: React.FC = () => {
  const { content, loading } = useContent();

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-white text-xl">Loading settings...</div>
      </div>
    );
  }

  const colSize = Math.ceil(content.binds.length / 3);
  const columns = [
    content.binds.slice(0, colSize),
    content.binds.slice(colSize, colSize * 2),
    content.binds.slice(colSize * 2),
  ];

  return (
    <div className="min-h-screen w-full">
      <div className="max-w-5xl mx-auto py-6 px-4 space-y-6">

        {/* Config Section — stacks on mobile, 3-col on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Config card */}
          <div className="bg-[#23232b] rounded-xl p-5 space-y-4 shadow border border-[#35353f] flex flex-col items-center">
            <a
              href={content.configSection.configLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-blue-600 text-white font-bold py-2 rounded mb-2 flex justify-center items-center"
            >
              Config
            </a>
            <div className="w-full bg-[#18181c] rounded p-3 text-gray-300 text-sm">{content.configSection.launchOptions}</div>
            <div className="w-full bg-[#18181c] rounded p-3 text-gray-300 text-xs">
              Crosshair<br /><span className="text-green-400">{content.configSection.crosshair}</span>
            </div>
            <div className="w-full bg-[#18181c] rounded p-3 text-gray-300 text-xs">
              Viewmodel<br /><span className="text-gray-400">{content.configSection.viewmodel}</span>
            </div>
            <div className="w-full bg-[#18181c] rounded p-3 text-gray-300 text-xs">
              HUD<br /><span className="text-gray-400">{content.configSection.hud}</span>
            </div>
            <div className="w-full bg-[#18181c] rounded p-3 text-gray-300 text-xs">
              Radar<br /><span className="text-gray-400">{content.configSection.radar}</span>
            </div>
          </div>

          {/* Mouse + Video + Advanced — full width on mobile, 2-col span on md+ */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-[#23232b] rounded-xl p-5 shadow border border-[#35353f]">

              {/* Mouse + Video — stack on mobile, side by side on md+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Mouse Settings */}
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-100 mb-3">Mouse Settings</h3>
                  <div className="space-y-2 text-sm text-gray-300">
                    {content.mouseSettings.map((setting) => (
                      <div key={setting.id} className="flex justify-between">
                        <span>{setting.label}</span>
                        <span>{setting.value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Video Settings */}
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-100 mb-3">Video Settings</h3>
                  <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between"><span>Brightness</span><span>88%</span></div>
                    <div className="flex justify-between"><span>Scaling mode</span><span>Stretched</span></div>
                    <div className="flex justify-between"><span>Aspect Ratio</span><span>4:3</span></div>
                    <div className="flex justify-between"><span>Resolution</span><span>1280x1024</span></div>
                    <div className="flex justify-between"><span>Display Mode</span><span>Fullscreen</span></div>
                    <div className="flex justify-between"><span>Refresh Rate</span><span>240 Hz</span></div>
                  </div>
                </div>
              </div>

              {/* Advanced Video — 2-col on sm+, single col on mobile */}
              <div className="mt-6">
                <h3 className="text-base sm:text-lg font-bold text-gray-100 mb-3">Advanced Video</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-300">
                  <div className="space-y-2">
                    {content.advancedVideoSettings
                      .slice(0, Math.ceil(content.advancedVideoSettings.length / 2))
                      .map((setting) => (
                        <div key={setting.id} className="flex justify-between">
                          <span>{setting.label}</span>
                          <span className={
                            setting.status === 'enabled'
                              ? 'text-green-400'
                              : setting.status === 'disabled'
                              ? 'text-red-400'
                              : ''
                          }>
                            {setting.value}
                          </span>
                        </div>
                      ))}
                  </div>
                  <div className="space-y-2">
                    {content.advancedVideoSettings
                      .slice(Math.ceil(content.advancedVideoSettings.length / 2))
                      .map((setting) => (
                        <div key={setting.id} className="flex justify-between">
                          <span>{setting.label}</span>
                          <span className={
                            setting.status === 'enabled'
                              ? 'text-green-400'
                              : setting.status === 'disabled'
                              ? 'text-red-400'
                              : ''
                          }>
                            {setting.value}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Binds Section — 1 col mobile, 2 col sm, 3 col md+ */}
        <div className="bg-[#23232b] rounded-xl p-5 shadow border border-[#35353f] flex flex-col items-center">
          <h3 className="text-base sm:text-lg font-bold text-gray-100 mb-4">Binds</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 text-sm text-gray-300 justify-items-center w-full">
            {columns.map((col, i) => (
              <div key={i} className="space-y-3 w-full">
                {col.map((bind) => (
                  <div key={bind.id} className="flex justify-between items-center sm:flex-col sm:items-center">
                    <span className="text-gray-400 sm:text-center">{bind.label}</span>
                    <span className="bg-[#18181c] px-2 py-1 rounded sm:mt-1 text-xs font-mono">{bind.key}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SettingsPage;